import PropTypes from "prop-types";
import classNames from "classnames";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import "./SavingsHighlight.scss";
import SeparatorLine from "app/components/ui/Separator/SeparatorLine";
import { FormattedMessage } from "react-intl";
import { TagFilledIcon } from "app/icons/finance";

const SavingsHighlight = ({
	messageKey,
	amountSaved,
	description,
	className,
	trackEvent,
	trackData,
	savingsList = [],
}) => {
	const { track } = useAnalytics();
	const popoverClassName = classNames("savings-highlight__popover", className);

	const mergedTrackData = {
		...trackData,
		discountValue: amountSaved,
	};

	// Track on click with the provided event name
	const handleClick = trackEvent ? () => track(trackEvent, mergedTrackData) : undefined;

	// Track on hover: auto-generates event name by replacing "_click_" with "_hover_"
	const handleHover = trackEvent
		? () => track(trackEvent.replace("_click_", "_hover_"), mergedTrackData)
		: undefined;

	const referenceContent = (
		<Typography
			variant={TYPOGRAPHY_VARIANTS.SMALL}
			className="savings-highlight__content"
			onClick={handleClick}
			onMouseEnter={handleHover}
		>
			<FormattedMessage
				id={messageKey}
				values={{
					price: <AmountContainer amount={amountSaved} />,
				}}
			/>
		</Typography>
	);

	// No description -> display static label only
	if (!description && savingsList.length === 0) {
		return (
			<div className="savings-highlight" data-testid="savings-highlight">
				<TagFilledIcon className="savings-highlight__icon" />
				{referenceContent}
			</div>
		);
	}

	return (
		<div className="savings-highlight" data-testid="savings-highlight">
			<TagFilledIcon className="savings-highlight__icon" />
			<FloatingButton
				position="bottom"
				referenceComponent={referenceContent}
				zIndexValue={100}
				floatingContent={
					<div className="savings-highlight__description-content">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							className="savings-highlight__description"
						>
							{description}
						</Typography>

						{savingsList.length > 0 && (
							<div className="savings-highlight__breakdown">
								<div className="savings-highlight__separator">
									<SeparatorLine />
								</div>
								<div className="savings-highlight__breakdown-list">
									{savingsList.map((saving, index) => (
										<div
											key={index}
											className="savings-highlight__breakdown-item"
										>
											<div className="savings-highlight__breakdown-label">
												<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
													{saving.label}
												</Typography>
											</div>
											<div className="savings-highlight__breakdown-amount">
												<AmountContainer amount={saving.amount} />
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				}
				popoverClassName={popoverClassName}
				dismissOnFloatingClick
				trigger="hover"
			/>
		</div>
	);
};

SavingsHighlight.propTypes = {
	messageKey: PropTypes.string,
	amountSaved: PropTypes.number,
	description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	className: PropTypes.string,
	trackEvent: PropTypes.string,
	trackData: PropTypes.object,
	savingsList: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
			amount: PropTypes.number.isRequired,
			details: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		})
	),
};

export default SavingsHighlight;
